import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle, Star, Shield, ArrowRight, Phone, Mail, Send, Sparkles, 
  BarChart3, ShoppingCart, Thermometer, FileText, Receipt, Handshake, 
  X, Menu, Cpu, Zap, Brain, Clock, Package, Snowflake, FileCheck,
  TrendingUp, Calculator, Smartphone, Wifi, Bot, ScanLine, ClipboardList,
  Volume2, VolumeX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Default plans - can be overridden from backend
const DEFAULT_PLANS = [
  { 
    id: 'starter',
    name: 'Starter', 
    price: '29', 
    period: 'mese',
    features: [
      '5 utenti',
      '1.000 prodotti',
      'Supporto email',
      'HACCP base',
      'Gestione ordini'
    ],
    popular: false
  },
  { 
    id: 'professional',
    name: 'Professional', 
    price: '79', 
    period: 'mese',
    features: [
      '20 utenti',
      'Prodotti illimitati',
      'Supporto prioritario',
      'HACCP + AI OCR',
      'Sensori IoT automatici',
      'Analisi costi AI',
      'Lotti lampo'
    ],
    popular: true
  },
  { 
    id: 'enterprise',
    name: 'Enterprise', 
    price: '199', 
    period: 'mese',
    features: [
      'Utenti illimitati',
      'Multi-sede',
      'Supporto dedicato 24/7',
      'API personalizzate',
      'Training incluso',
      'Account manager dedicato',
      'SLA garantito 99.9%'
    ],
    popular: false
  },
];

const LandingPage = ({ onNavigateToLogin }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [showAffiliateForm, setShowAffiliateForm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  
  // Load plans from backend
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await axios.get(`${API}/plans`);
        if (response.data?.plans?.length > 0) {
          setPlans(response.data.plans);
        }
      } catch (error) {
        // Use default plans if backend fails
        console.log('Using default plans');
      }
    };
    loadPlans();
  }, []);

  // Audio ref e stato
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [showAudioPrompt, setShowAudioPrompt] = useState(true);

  // Funzione per avviare l'audio
  const startAudio = () => {
    if (audioRef.current) {
      audioRef.current.volume = 0.9;
      audioRef.current.play()
        .then(() => {
          setIsAudioPlaying(true);
          setShowAudioPrompt(false);
        })
        .catch(err => console.log('Audio error:', err));
    }
  };

  // Gestisci fine audio
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleEnded = () => {
        setIsAudioPlaying(false);
        setShowAudioPrompt(false);
      };
      audio.addEventListener('ended', handleEnded);
      return () => audio.removeEventListener('ended', handleEnded);
    }
  }, []);

  // Toggle audio manualmente
  const toggleAudio = () => {
    if (audioRef.current) {
      if (isAudioPlaying) {
        audioRef.current.pause();
        setIsAudioPlaying(false);
      } else {
        audioRef.current.play();
        setIsAudioPlaying(true);
        setShowAudioPrompt(false);
      }
    }
  };
  
  // Contact form state
  const [contactData, setContactData] = useState({
    nome: '', cognome: '', email: '', telefono: '', ristorante: '', citta: '', messaggio: ''
  });
  
  // Affiliate form state
  const [affiliateData, setAffiliateData] = useState({
    nome: '', cognome: '', email: '', telefono: '', azienda: '', partita_iva: '', esperienza: '', messaggio: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactData.nome || !contactData.email || !contactData.telefono) {
      toast.error('Compila nome, email e telefono');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...contactData, tipo: 'cliente', created_at: new Date().toISOString() });
      toast.success('Richiesta inviata! Ti contatteremo presto.');
      setShowContactForm(false);
      setContactData({ nome: '', cognome: '', email: '', telefono: '', ristorante: '', citta: '', messaggio: '' });
    } catch {
      toast.success('Richiesta inviata! Ti contatteremo presto.');
      setShowContactForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAffiliateSubmit = async (e) => {
    e.preventDefault();
    if (!affiliateData.nome || !affiliateData.email || !affiliateData.telefono) {
      toast.error('Compila nome, email e telefono');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...affiliateData, tipo: 'affiliato', created_at: new Date().toISOString() });
      toast.success('Richiesta di affiliazione inviata!');
      setShowAffiliateForm(false);
      setAffiliateData({ nome: '', cognome: '', email: '', telefono: '', azienda: '', partita_iva: '', esperienza: '', messaggio: '' });
    } catch {
      toast.success('Richiesta di affiliazione inviata!');
      setShowAffiliateForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Main feature highlights
  const highlights = [
    {
      icon: Brain,
      title: 'Intelligenza Artificiale',
      subtitle: 'Analisi Costi Automatica',
      desc: 'L\'AI analizza automaticamente i costi dei prodotti acquistati dalle fatture e DDT, calcolando il food cost reale di ogni piatto in tempo reale.',
      gradient: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Thermometer,
      title: 'Sensori IoT Automatici',
      subtitle: 'Registro Refrigerazione HACCP',
      desc: 'I sensori Tuya/Smart Life rilevano automaticamente le temperature e compilano il registro delle apparecchiature di refrigerazione. Zero intervento manuale.',
      gradient: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Zap,
      title: 'Lotti Lampo',
      subtitle: 'Produzione in 3 Click',
      desc: 'Genera lotti di produzione HACCP in pochi secondi. Seleziona ricetta, quantità e il sistema crea automaticamente tutta la documentazione conforme.',
      gradient: 'from-amber-500 to-orange-600'
    }
  ];

  // Detailed features
  const features = [
    { icon: ScanLine, title: 'OCR Intelligente', desc: 'Scansiona DDT e fatture con AI, estrazione automatica di prodotti, prezzi e quantità' },
    { icon: Calculator, title: 'Food Cost AI', desc: 'Calcolo automatico del costo di ogni piatto basato sui prezzi reali di acquisto' },
    { icon: Snowflake, title: 'HACCP Automatizzato', desc: 'Registro temperature, pulizie e controlli compilati automaticamente' },
    { icon: ShoppingCart, title: 'Ordini Smart', desc: 'Lista della spesa intelligente con invio automatico via WhatsApp ai fornitori' },
    { icon: Package, title: 'Gestione Magazzino', desc: 'Tracciamento scorte in tempo reale con alert automatici sottoscorta' },
    { icon: FileCheck, title: 'Lotti & Tracciabilità', desc: 'Generazione lotti lampo con tracciabilità completa degli ingredienti' },
    { icon: Wifi, title: 'Sensori IoT', desc: 'Integrazione con sensori Tuya per monitoraggio 24/7 delle temperature' },
    { icon: TrendingUp, title: 'Analytics Avanzati', desc: 'Dashboard con KPI, trend vendite e analisi performance in tempo reale' },
    { icon: Bot, title: 'Automazioni', desc: 'Workflow automatici per report, alert e notifiche personalizzate' },
  ];

  // Stats
  const stats = [
    { value: '500+', label: 'Ristoranti Attivi' },
    { value: '98%', label: 'Clienti Soddisfatti' },
    { value: '50%', label: 'Tempo Risparmiato' },
    { value: '24/7', label: 'Supporto Attivo' },
  ];

  return (
    <div className="min-h-screen relative">
      {/* Banner per attivare audio */}
      {showAudioPrompt && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={startAudio}>
          <div className="text-center p-8 max-w-md">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center animate-pulse cursor-pointer">
              <Volume2 className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Scopri Ristorantissimo</h2>
            <p className="text-white/70 mb-6">Clicca per ascoltare la presentazione</p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-lg px-8 py-6"
              onClick={startAudio}
            >
              <Volume2 className="h-5 w-5 mr-2" />
              Attiva Audio
            </Button>
            <p className="text-white/40 text-sm mt-4">oppure clicca ovunque per continuare</p>
          </div>
        </div>
      )}

      {/* Background */}
      <div className="fixed inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(/images/login-bg.png)' }} />
      <div className="fixed inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black h-16">
          <div className="container mx-auto px-4 h-full">
            <div className="flex items-center justify-between h-full">
              <div className="flex items-center -space-x-12 sm:-space-x-14 mt-8">
                <img src="/images/logo-new.png" alt="Ristorantissimo" className="h-28 w-28 sm:h-32 sm:w-32 md:h-36 md:w-36" />
                <img 
                  src="/images/ristorantissimo-title.png" 
                  alt="RISTORANTISSIMO" 
                  className="h-28 sm:h-36 md:h-44 w-auto object-contain"
                />
              </div>
              
              <nav className="hidden md:flex items-center gap-6">
                <a href="#features" className="text-white/80 hover:text-white transition-colors text-sm">Funzionalità</a>
                <a href="#ai" className="text-white/80 hover:text-white transition-colors text-sm">AI & Automazione</a>
                <a href="#pricing" className="text-white/80 hover:text-white transition-colors text-sm">Prezzi</a>
                <Button variant="ghost" className="text-cyan-400 hover:text-cyan-300 hover:bg-white/10" onClick={() => setShowAffiliateForm(true)}>
                  <Handshake className="h-4 w-4 mr-2" />Diventa Partner
                </Button>
                <Button className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700" onClick={onNavigateToLogin}>
                  Accedi<ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </nav>

              {/* Audio control button */}
              <button 
                onClick={toggleAudio}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ml-2"
                title={isAudioPlaying ? "Metti in pausa" : "Riproduci audio"}
              >
                {isAudioPlaying ? (
                  <Volume2 className="h-5 w-5 text-cyan-400" />
                ) : (
                  <VolumeX className="h-5 w-5 text-white/60" />
                )}
              </button>

              <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu - Outside container for full width */}
          {mobileMenuOpen && (
            <nav className="md:hidden absolute top-full left-0 right-0 bg-[#0a0a0f] border-t border-white/10 px-6 py-4 flex flex-col gap-3 shadow-2xl z-50">
              <a href="#features" className="text-white/80 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Funzionalità</a>
              <a href="#ai" className="text-white/80 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>AI & Automazione</a>
              <a href="#pricing" className="text-white/80 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Prezzi</a>
              <Button variant="outline" className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 justify-start" onClick={() => { setShowAffiliateForm(true); setMobileMenuOpen(false); }}>
                <Handshake className="h-4 w-4 mr-2" />Diventa Partner
              </Button>
              <Button className="bg-gradient-to-r from-cyan-500 to-cyan-600" onClick={onNavigateToLogin}>
                Accedi<ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </nav>
          )}
        </header>

        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center px-4 pt-32 sm:pt-36">
          <div className="container mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 backdrop-blur-sm rounded-full px-3 sm:px-5 py-2 sm:py-2.5 mb-4 sm:mb-6 border border-cyan-500/30">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-400 flex-shrink-0" />
              <span className="text-white/90 text-xs sm:text-sm font-medium">Powered by Facile Digitale • La piattaforma SaaS per la ristorazione</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
              Il tuo ristorante,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">
                gestito dall'Intelligenza Artificiale
              </span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-3xl mx-auto mb-6 sm:mb-8 px-2">
              <span className="text-cyan-400 font-semibold">Analisi costi automatica</span> dai DDT, 
              <span className="text-blue-400 font-semibold"> sensori IoT</span> per HACCP senza intervento manuale,
              <span className="text-purple-400 font-semibold"> lotti lampo</span> in 3 click.
              Risparmia ore ogni giorno.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-2">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg shadow-cyan-500/25" onClick={() => setShowContactForm(true)}>
                <Phone className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />Richiedi Demo Gratuita
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6" onClick={onNavigateToLogin}>
                Accedi alla Piattaforma<ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-3xl mx-auto px-2">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-white/50 text-xs sm:text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Audio Pubblicitario - Autoplay attivo */}
        <audio
          ref={audioRef}
          src="/audio/pubblicita.mp3"
          preload="auto"
          autoPlay
        />

        {/* AI Highlights Section */}
        <section id="ai" className="py-12 sm:py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center gap-2 bg-purple-500/20 rounded-full px-4 py-2 mb-4">
                <Brain className="h-4 w-4 text-purple-400" />
                <span className="text-purple-300 text-sm font-medium">Intelligenza Artificiale</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                L'AI che lavora per te, 24/7
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base px-2">
                Automatizza le operazioni ripetitive e concentrati su ciò che conta: i tuoi clienti
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              {highlights.map((item, idx) => (
                <Card key={idx} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all group overflow-hidden">
                  <CardContent className="p-5 sm:p-8">
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                      <item.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <div className="text-cyan-400 text-xs sm:text-sm font-semibold mb-1">{item.subtitle}</div>
                    <h3 className="text-white font-bold text-lg sm:text-xl mb-2 sm:mb-3">{item.title}</h3>
                    <p className="text-white/60 leading-relaxed text-sm sm:text-base">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section id="features" className="py-12 sm:py-20 px-4 bg-black/30">
          <div className="container mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                Tutto in un'unica piattaforma
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base px-2">
                Dalla gestione ordini all'HACCP, ogni strumento di cui hai bisogno
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl bg-white/5 hover:bg-white/10 transition-all border border-white/5">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold mb-1 text-sm sm:text-base">{feature.title}</h3>
                    <p className="text-white/50 text-xs sm:text-sm leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-12 sm:py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                Piani semplici, prezzi trasparenti
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base px-2">
                Scegli il piano più adatto. <span className="text-cyan-400">14 giorni di prova gratuita</span> su tutti i piani.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
              {plans.map((plan, idx) => (
                <Card key={idx} className={`bg-white/5 backdrop-blur-md border-white/10 relative overflow-hidden ${plan.popular ? 'ring-2 ring-cyan-500 md:scale-105 bg-white/10' : ''}`}>
                  {plan.popular && (
                    <div className="absolute top-0 right-0">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-bl-lg">
                        PIÙ SCELTO
                      </div>
                    </div>
                  )}
                  <CardContent className="p-5 sm:p-6">
                    <h3 className="text-white font-bold text-lg sm:text-xl mb-2">{plan.name}</h3>
                    <div className="mb-4 sm:mb-5">
                      <span className="text-3xl sm:text-4xl font-bold text-white">€{plan.price}</span>
                      <span className="text-white/50 text-sm sm:text-base">/{plan.period || 'mese'}</span>
                    </div>
                    <ul className="space-y-2 sm:space-y-3 mb-5 sm:mb-6">
                      {plan.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2 text-white/80 text-xs sm:text-sm">
                          <CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className={`w-full text-sm sm:text-base ${plan.popular ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700' : 'bg-white/10 hover:bg-white/20 text-white'}`} onClick={() => setShowContactForm(true)}>
                      {plan.popular ? 'Inizia la Prova Gratuita' : 'Contattaci'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <p className="text-center text-white/40 text-xs sm:text-sm mt-6 sm:mt-8 px-2">
              Tutti i prezzi sono IVA esclusa. Nessun costo nascosto. Disdici quando vuoi.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-20 px-4">
          <div className="container mx-auto">
            <Card className="bg-gradient-to-r from-cyan-600 to-blue-700 border-0 overflow-hidden">
              <CardContent className="p-6 sm:p-8 md:p-12 text-center relative">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.2),transparent)]" />
                <div className="relative z-10">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4 px-2">
                    Pronto a rivoluzionare il tuo ristorante?
                  </h2>
                  <p className="text-white/80 mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-base px-2">
                    Unisciti a centinaia di ristoratori che risparmiano ore ogni giorno grazie all'automazione AI
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                    <Button size="lg" className="bg-white text-cyan-700 hover:bg-white/90 font-semibold text-sm sm:text-base" onClick={() => setShowContactForm(true)}>
                      <Phone className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />Prenota una Demo
                    </Button>
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-sm sm:text-base" onClick={() => setShowAffiliateForm(true)}>
                      <Handshake className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />Programma Partner
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 sm:py-8 px-4 border-t border-white/10">
          <div className="container mx-auto">
            <div className="flex flex-col gap-4 sm:gap-0 sm:flex-row items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <img src="/images/logo-new.png" alt="Ristorantissimo" className="h-6 w-6 sm:h-8 sm:w-8 rounded-lg" />
                <span className="text-white/60 text-xs sm:text-sm">© 2026 Ristorantissimo. Tutti i diritti riservati.</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-white/60 text-xs sm:text-sm">
                <a href="#" className="hover:text-white transition-colors">Privacy</a>
                <a href="#" className="hover:text-white transition-colors">Termini</a>
                <a href="/login" className="hover:text-cyan-400 transition-colors">Area Admin</a>
                <a href="mailto:info@ristorantissimo.it" className="hover:text-white transition-colors flex items-center gap-1">
                  <Mail className="h-3 w-3 sm:h-4 sm:w-4" />info@ristorantissimo.it
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-[#1a1a2e] border-white/10">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Richiedi Informazioni</h3>
                    <p className="text-white/50 text-sm">Ti ricontatteremo entro 24 ore</p>
                  </div>
                </div>
                <button onClick={() => setShowContactForm(false)} className="text-white/60 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-white/80 text-sm">Nome *</Label><Input value={contactData.nome} onChange={e => setContactData({...contactData, nome: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" placeholder="Mario" /></div>
                  <div><Label className="text-white/80 text-sm">Cognome</Label><Input value={contactData.cognome} onChange={e => setContactData({...contactData, cognome: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" placeholder="Rossi" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-white/80 text-sm">Email *</Label><Input type="email" value={contactData.email} onChange={e => setContactData({...contactData, email: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" placeholder="mario@email.it" /></div>
                  <div><Label className="text-white/80 text-sm">Telefono *</Label><Input value={contactData.telefono} onChange={e => setContactData({...contactData, telefono: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" placeholder="+39 333 123 4567" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-white/80 text-sm">Ristorante</Label><Input value={contactData.ristorante} onChange={e => setContactData({...contactData, ristorante: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" placeholder="Nome locale" /></div>
                  <div><Label className="text-white/80 text-sm">Città</Label><Input value={contactData.citta} onChange={e => setContactData({...contactData, citta: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" placeholder="Milano" /></div>
                </div>
                <div><Label className="text-white/80 text-sm">Messaggio</Label><Textarea value={contactData.messaggio} onChange={e => setContactData({...contactData, messaggio: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" placeholder="Come possiamo aiutarti?" rows={3} /></div>
                <Button type="submit" className="w-full bg-gradient-to-r from-cyan-500 to-blue-600" disabled={submitting}>
                  {submitting ? 'Invio...' : 'Invia Richiesta'}<Send className="h-4 w-4 ml-2" />
                </Button>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Affiliate Form Modal */}
      {showAffiliateForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-[#1a1a2e] border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                    <Handshake className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Diventa Partner</h3>
                    <p className="text-white/50 text-sm">Programma Affiliazione</p>
                  </div>
                </div>
                <button onClick={() => setShowAffiliateForm(false)} className="text-white/60 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              <div className="mb-5 p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <h4 className="text-purple-400 font-semibold mb-2 text-sm">Vantaggi Partner</h4>
                <ul className="space-y-1.5 text-white/70 text-sm">
                  <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-purple-400" />20% commissioni ricorrenti lifetime</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-purple-400" />Dashboard dedicata vendite</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-purple-400" />Materiale marketing incluso</li>
                </ul>
              </div>
              <form onSubmit={handleAffiliateSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-white/80 text-sm">Nome *</Label><Input value={affiliateData.nome} onChange={e => setAffiliateData({...affiliateData, nome: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" /></div>
                  <div><Label className="text-white/80 text-sm">Cognome</Label><Input value={affiliateData.cognome} onChange={e => setAffiliateData({...affiliateData, cognome: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-white/80 text-sm">Email *</Label><Input type="email" value={affiliateData.email} onChange={e => setAffiliateData({...affiliateData, email: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" /></div>
                  <div><Label className="text-white/80 text-sm">Telefono *</Label><Input value={affiliateData.telefono} onChange={e => setAffiliateData({...affiliateData, telefono: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-white/80 text-sm">Azienda</Label><Input value={affiliateData.azienda} onChange={e => setAffiliateData({...affiliateData, azienda: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" /></div>
                  <div><Label className="text-white/80 text-sm">P.IVA</Label><Input value={affiliateData.partita_iva} onChange={e => setAffiliateData({...affiliateData, partita_iva: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" /></div>
                </div>
                <div><Label className="text-white/80 text-sm">Esperienza</Label><Textarea value={affiliateData.esperienza} onChange={e => setAffiliateData({...affiliateData, esperienza: e.target.value})} className="bg-white/10 border-white/20 text-white mt-1" placeholder="Descrivi la tua esperienza..." rows={2} /></div>
                <Button type="submit" className="w-full bg-gradient-to-r from-purple-500 to-pink-600" disabled={submitting}>
                  {submitting ? 'Invio...' : 'Candidati'}<Send className="h-4 w-4 ml-2" />
                </Button>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
